import DateInput from '../../components/DateInput';
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { toast } from 'sonner';
import { GraduationCap, Plus, Play, Square, Trash2, Award, X, Loader2, Edit2, Sparkles, CalendarDays, Heart } from 'lucide-react';

const BLANK = {
  kind: 'student',
  name: '', student_id: '', school: '', academic_year: '',
  clinical_area: '', start_date: '', end_date: '',
  supervisor_name: '', supervisor_title: '', target_hours: 200, notes: '',
  dob: '', gender: '', id_type: 'national_id', area_placements: [],
  phone: '', email: '', address: '',
  emergency_name: '', emergency_phone: '', emergency_relationship: '',
  is_minor: false, parent_name: '', parent_phone: '', parent_email: '',
};

const ID_TYPES = [
  { value: 'national_id', label: 'National ID (e.g. PESEL)' },
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: "Driver's licence" },
  { value: 'student_card', label: 'Student card' },
  { value: 'other', label: 'Other' },
];

export function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
}

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [areas, setAreas] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showCert, setShowCert] = useState(null);
  const [showLog, setShowLog] = useState(null);

  const load = () => api.get('/students').then(r => setStudents(r.data));
  useEffect(() => {
    load();
    api.get('/students/clinical-areas').then(r => setAreas(r.data));
  }, []);

  const clockIn = async (s) => {
    try {
      await api.post(`/students/${s.student_id}/clock-in`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not clock in.';
      // 400 = "Session already in progress" — the common, non-fatal case.
      alert(msg);
    }
  };
  const clockOut = async (s) => {
    try {
      await api.post(`/students/${s.student_id}/clock-out`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not clock out.';
      // 404 = "No open session" — they tried to clock-out without a clock-in.
      alert(msg);
    }
  };
  const remove = async (s) => {
    if (!(await safeConfirm(`Delete ${s.name}? All practical sessions will be removed.`))) return;
    try {
      await api.delete(`/students/${s.student_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">EDUCATION</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">University/School students & volunteers</h1>
          <p className="text-fg-muted mt-2">Track placement hours per session-day; AI generates a clinical summary for the printable certificate.</p>
        </div>
        <button onClick={()=>setEdit({...BLANK})} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="new-student-btn">
          <Plus className="w-4 h-4"/> New student / volunteer
        </button>
      </div>

      <div className="admin-card">
        <table>
          <thead><tr><th>Name · school/role</th><th>Clinical area</th><th>Hours</th><th>Period</th><th>Supervisor</th><th></th></tr></thead>
          <tbody>
            {students.map(s => (
              <tr key={s.student_id} data-testid={`student-row-${s.student_id}`}>
                <td>
                  <div className="font-medium inline-flex items-center gap-2">
                    {s.name}
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.kind === 'volunteer' ? 'bg-[#FFF4D6] text-[#7A5500]' : 'bg-brand/10 text-brand'}`}>
                      {s.kind || 'student'}
                    </span>
                  </div>
                  <div className="text-[11px] text-fg-muted">
                    {s.kind === 'volunteer'
                      ? `ID ${s.student_id}${s.email ? ' · ' + s.email : ''}`
                      : `${s.school || 'School?'} · ${s.academic_year || ''} · ID ${s.student_id}`}
                  </div>
                </td>
                <td className="text-xs">{s.clinical_area}</td>
                <td>
                  <div className="text-sm font-mono">{s.completed_hours}/{s.target_hours} h</div>
                  <div className="h-2 bg-soft rounded-full mt-1 overflow-hidden max-w-[140px]">
                    <div className="h-full bg-brand transition-all" style={{ width: `${s.completed_pct}%` }}/>
                  </div>
                </td>
                <td className="text-xs">{s.start_date} → {s.end_date || '—'}</td>
                <td className="text-xs">{s.supervisor_title || s.supervisor_name || '—'}</td>
                <td>
                  <div className="flex gap-2 text-xs">
                    {s.is_clocked_in ? (
                      <button onClick={()=>clockOut(s)} className="text-warn inline-flex items-center gap-1 font-medium" data-testid={`clock-out-${s.student_id}`} title="Currently clocked in — click to clock out">
                        <Square className="w-3.5 h-3.5 fill-current"/> Stop
                      </button>
                    ) : (
                      <button onClick={()=>clockIn(s)} className="text-brand inline-flex items-center gap-1" data-testid={`clock-in-${s.student_id}`}>
                        <Play className="w-3.5 h-3.5"/> Start
                      </button>
                    )}
                    <button onClick={()=>setShowLog(s)} className="text-brand inline-flex items-center gap-1" data-testid={`log-${s.student_id}`}>
                      <CalendarDays className="w-3.5 h-3.5"/> Log
                    </button>
                    <button onClick={()=>setShowCert(s)} className="text-brand inline-flex items-center gap-1" data-testid={`cert-${s.student_id}`}>
                      <Award className="w-3.5 h-3.5"/> Cert
                    </button>
                    <button onClick={()=>setEdit(s)} className="text-brand" data-testid={`edit-student-${s.student_id}`}>
                      <Edit2 className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>remove(s)} className="text-[#B83A3A]" data-testid={`del-student-${s.student_id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!students.length && <tr><td colSpan="6" className="text-center py-8 text-fg-muted">No students or volunteers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <EditModal student={edit} areas={areas} onClose={()=>setEdit(null)} onAutoSaved={load} onSaved={()=>{ load(); setEdit(null); }}/>}
      {showLog && <SessionLogModal student={showLog} areas={areas} onClose={()=>{ setShowLog(null); load(); }}/>}
      {showCert && <CertificateModal student={showCert} onClose={()=>setShowCert(null)}/>}
    </div>
  );
}

function EditModal({ student, areas, onClose, onSaved, onAutoSaved }) {
  const isNew = !student.created_at;
  const [form, setForm] = useState({ ...BLANK, ...student });
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState('');
  const firstRun = useRef(true);
  const autoRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isStudent = form.kind === 'student';
  const isVolunteer = form.kind === 'volunteer';
  const age = calcAge(form.dob);

  const setDob = (v) => {
    setForm(f => {
      const a = calcAge(v);
      return { ...f, dob: v, ...(f.kind === 'student' && a !== null ? { is_minor: a < 18 } : {}) };
    });
  };

  // Autosave — existing records only; debounce 1.2s after last edit.
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (isNew) return;
    if (!form.name || !form.student_id || !form.clinical_area || !form.start_date) return;
    if (autoRef.current) clearTimeout(autoRef.current);
    autoRef.current = setTimeout(async () => {
      setAutoState('saving');
      try {
        await api.put(`/students/${student.student_id}`, form);
        setAutoState('saved');
        onAutoSaved?.();
      } catch { setAutoState('error'); }
    }, 1200);
    return () => clearTimeout(autoRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post('/students', form);
      else await api.put(`/students/${student.student_id}`, form);
      onSaved();
    } catch (e) { toast.error('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="student-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h2 className="font-heading font-semibold">{isNew ? 'New record' : `Edit · ${student.name}`}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Kind selector */}
          <div>
            <div className="text-xs uppercase tracking-wider text-fg-muted mb-2">This record is for</div>
            <div className="flex gap-2" data-testid="s-kind">
              {['student', 'volunteer'].map(k => (
                <button key={k} type="button"
                        onClick={() => set('kind', k)}
                        className={`flex-1 px-4 py-3 rounded-lg border text-sm transition ${form.kind === k ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand/40'}`}
                        data-testid={`s-kind-${k}`}>
                  {k === 'student'
                    ? <><GraduationCap className="w-4 h-4 inline mr-2"/> University/School student</>
                    : <><Heart className="w-4 h-4 inline mr-2"/> Volunteer</>}
                </button>
              ))}
            </div>
          </div>

          <Section title="Identity">
            <Grid>
              <Field label={`${isStudent ? 'Student' : 'Volunteer'} name *`}><input className="input" value={form.name} onChange={e=>set('name', e.target.value)} data-testid="s-name"/></Field>
              {isStudent ? (
                <Field label="Student ID *"><input className="input" value={form.student_id} onChange={e=>set('student_id', e.target.value)} disabled={!isNew} data-testid="s-id"/></Field>
              ) : (
                <Field label="ID number *">
                  <div className="flex gap-2">
                    <select className="input !w-[46%]" value={form.id_type || 'national_id'} onChange={e=>set('id_type', e.target.value)} data-testid="s-id-type">
                      {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input className="input flex-1" value={form.student_id} onChange={e=>set('student_id', e.target.value)} disabled={!isNew} placeholder="ID #" data-testid="s-id"/>
                  </div>
                </Field>
              )}
              <Field label="Date of birth">
                <div className="flex items-center gap-2">
                  <DateInput className="flex-1" value={form.dob || ''} onChange={e=>setDob(e.target.value)} data-testid="s-dob"/>
                  {age !== null && (
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${age < 18 ? 'bg-[#FFF4D6] text-[#7A5500]' : 'bg-brand/10 text-brand'}`} data-testid="s-age">
                      Age {age}{age < 18 ? ' · minor' : ''}
                    </span>
                  )}
                </div>
              </Field>
              <Field label="Gender">
                <select className="input" value={form.gender || ''} onChange={e=>set('gender', e.target.value)} data-testid="s-gender">
                  <option value="">— select —</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </Field>
              {isStudent && (
                <label className="flex items-center gap-2 mt-6 text-sm">
                  <input type="checkbox" checked={!!form.is_minor} onChange={e=>set('is_minor', e.target.checked)} data-testid="s-minor"/>
                  Under 18 (school student) — parent contact required
                </label>
              )}
              <Field label="Phone"><input className="input" value={form.phone || ''} onChange={e=>set('phone', e.target.value)} data-testid="s-phone"/></Field>
              <Field label="Email"><input className="input" type="email" value={form.email || ''} onChange={e=>set('email', e.target.value)} data-testid="s-email"/></Field>
              <Field label="Address" full><input className="input" value={form.address || ''} onChange={e=>set('address', e.target.value)} placeholder="Street, city, postal code" data-testid="s-address"/></Field>
            </Grid>
          </Section>

          {/* Student-only school block */}
          {isStudent && (
            <Section title="School / programme">
              <Grid>
                <Field label="University / school *"><input className="input" value={form.school} onChange={e=>set('school', e.target.value)} data-testid="s-school"/></Field>
                <Field label="Academic year *"><input className="input" value={form.academic_year} placeholder="2025-2026" onChange={e=>set('academic_year', e.target.value)} data-testid="s-year"/></Field>
              </Grid>
            </Section>
          )}

          {/* Parent block — only for under-18 students */}
          {isStudent && form.is_minor && (
            <Section title="Parent / legal guardian">
              <Grid>
                <Field label="Parent name *"><input className="input" value={form.parent_name || ''} onChange={e=>set('parent_name', e.target.value)} data-testid="s-parent-name"/></Field>
                <Field label="Parent phone *"><input className="input" value={form.parent_phone || ''} onChange={e=>set('parent_phone', e.target.value)} data-testid="s-parent-phone"/></Field>
                <Field label="Parent email" full><input className="input" type="email" value={form.parent_email || ''} onChange={e=>set('parent_email', e.target.value)} data-testid="s-parent-email"/></Field>
              </Grid>
            </Section>
          )}

          <Section title="Emergency contact">
            <Grid>
              <Field label="Contact name"><input className="input" value={form.emergency_name || ''} onChange={e=>set('emergency_name', e.target.value)} data-testid="s-emergency-name"/></Field>
              <Field label="Contact phone"><input className="input" value={form.emergency_phone || ''} onChange={e=>set('emergency_phone', e.target.value)} data-testid="s-emergency-phone"/></Field>
              <Field label="Relationship"><input className="input" value={form.emergency_relationship || ''} onChange={e=>set('emergency_relationship', e.target.value)} placeholder="Parent, spouse, friend…" data-testid="s-emergency-rel"/></Field>
            </Grid>
          </Section>

          {/* Volunteer-only area placements */}
          {isVolunteer && (
            <Section title="Area placements">
              <div className="text-xs text-fg-muted mb-2">Select every clinical area where this volunteer is placed. The session log tracks hours per area.</div>
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto pr-1" data-testid="s-area-placements">
                {areas.map(a => {
                  const on = (form.area_placements || []).includes(a);
                  return (
                    <button key={a} type="button"
                            onClick={() => set('area_placements', on ? (form.area_placements || []).filter(x => x !== a) : [...(form.area_placements || []), a])}
                            className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? 'bg-brand text-white border-brand' : 'border-soft text-fg-secondary hover:border-brand/40'}`}
                            data-testid={`s-placement-${a.slice(0,12).replace(/[^a-z]/gi,'').toLowerCase()}`}>
                      {a}
                    </button>
                  );
                })}
              </div>
              {(form.area_placements || []).length > 0 && (
                <div className="text-[11px] text-fg-muted mt-2">{form.area_placements.length} area(s) selected</div>
              )}
            </Section>
          )}

          <Section title="Placement details">
            <Grid>
              <Field label="Clinical area *" full>
                <select className="input" value={form.clinical_area} onChange={e=>set('clinical_area', e.target.value)} data-testid="s-area">
                  <option value="">— select —</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Start date *"><DateInput value={form.start_date || ''} onChange={e=>set('start_date', e.target.value)} data-testid="s-start"/></Field>
              <Field label="End date"><DateInput value={form.end_date || ''} onChange={e=>set('end_date', e.target.value)} data-testid="s-end"/></Field>
              <Field label="Target hours"><input className="input" type="number" value={form.target_hours} onChange={e=>set('target_hours', parseInt(e.target.value) || 0)} data-testid="s-target"/></Field>
              <Field label="Supervisor name"><input className="input" value={form.supervisor_name || ''} onChange={e=>set('supervisor_name', e.target.value)} data-testid="s-supervisor"/></Field>
              <Field label="Supervisor professional title"><input className="input" value={form.supervisor_title || ''} onChange={e=>set('supervisor_title', e.target.value)} placeholder="PT, DPT, MCMT…" data-testid="s-supervisor-title"/></Field>
              <Field label="Notes" full><textarea className="input min-h-[60px]" value={form.notes || ''} onChange={e=>set('notes', e.target.value)}/></Field>
            </Grid>
          </Section>
        </div>

        <div className="px-5 pb-5 flex items-center justify-end gap-2 border-t border-soft pt-4">
          {!isNew && (
            <span className={`text-xs mr-auto ${autoState === 'error' ? 'text-[#B83A3A]' : 'text-fg-muted'}`} data-autostate={autoState} data-testid="s-autosave-state" data-no-translate>
              {autoState === 'saving' ? 'Autosaving…' : autoState === 'saved' ? 'Autosaved ✓' : autoState === 'error' ? 'Autosave failed — check required fields' : 'Autosave is on — edits save automatically'}
            </span>
          )}
          <button onClick={onClose} className="btn-secondary text-sm">{isNew ? 'Cancel' : 'Close'}</button>
          <button onClick={save} disabled={saving || !form.name || !form.student_id || !form.clinical_area || !form.start_date} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="s-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
            {saving ? 'Saving…' : (isNew ? 'Create' : 'Save & close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionLogModal({ student, areas, onClose }) {
  const [log, setLog] = useState([]);
  const [draft, setDraft] = useState({ date: new Date().toISOString().slice(0, 10), clinical_area: student.clinical_area || '', hours: '', note: '', supervisor_name: student.supervisor_name || '' });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/students/${student.student_id}/log`).then(r => setLog(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.date || !draft.hours) return;
    setSaving(true);
    try {
      await api.post(`/students/${student.student_id}/log`, {
        ...draft, hours: parseFloat(draft.hours) || 0,
      });
      setDraft({ ...draft, hours: '', note: '' });
      load();
    } catch (e) { alert('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  const del = async (l) => {
    if (!(await safeConfirm('Delete this log entry?'))) return;
    await api.delete(`/students/log/${l.log_id}`);
    load();
  };
  const total = log.reduce((s, r) => s + (r.hours || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="log-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h2 className="font-heading font-semibold inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4"/> {student.name} — session log
          </h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Add row */}
          <div className="surface border border-soft rounded-lg p-3 grid md:grid-cols-6 gap-2">
            <DateInput className="text-sm md:col-span-1" value={draft.date} onChange={e => setDraft({...draft, date: e.target.value})} data-testid="log-date"/>
            <select className="input text-sm md:col-span-2" value={draft.clinical_area} onChange={e => setDraft({...draft, clinical_area: e.target.value})} data-testid="log-area">
              <option value="">— area —</option>
              {areas.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <input type="number" step="0.5" min="0" className="input text-sm" placeholder="hours" value={draft.hours} onChange={e => setDraft({...draft, hours: e.target.value})} data-testid="log-hours"/>
            <input className="input text-sm md:col-span-2" placeholder="Supervisor (optional)" value={draft.supervisor_name} onChange={e => setDraft({...draft, supervisor_name: e.target.value})} data-testid="log-supervisor"/>
            <textarea className="input text-sm md:col-span-5 min-h-[44px]" placeholder="Note / what was practised today" value={draft.note} onChange={e => setDraft({...draft, note: e.target.value})} data-testid="log-note"/>
            <button onClick={add} disabled={saving || !draft.date || !draft.hours} className="btn-primary text-sm md:col-span-1" data-testid="log-add">Add</button>
          </div>

          {/* List */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-fg-muted border-b border-soft">
                <tr><th className="text-left py-2">Date</th><th className="text-left">Area</th><th className="text-right">Hours</th><th className="text-left">Note</th><th className="text-left">Supervisor</th><th></th></tr>
              </thead>
              <tbody>
                {log.map(l => (
                  <tr key={l.log_id} className="border-b border-soft/50" data-testid={`log-row-${l.log_id}`}>
                    <td className="py-2 font-mono text-xs">{l.date}</td>
                    <td className="text-xs">{l.clinical_area}</td>
                    <td className="text-right font-mono">{l.hours}</td>
                    <td className="text-xs text-fg-muted line-clamp-2 max-w-[260px]">{l.note}</td>
                    <td className="text-xs">{l.supervisor_name || '—'}</td>
                    <td><button onClick={() => del(l)} className="text-[#B83A3A]"><Trash2 className="w-3.5 h-3.5"/></button></td>
                  </tr>
                ))}
                {!log.length && <tr><td colSpan={6} className="text-center py-6 text-fg-muted">No session entries yet — add one above.</td></tr>}
              </tbody>
              {log.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-soft font-semibold">
                    <td colSpan={2} className="py-2 text-right uppercase text-xs tracking-wider">Grand total</td>
                    <td className="text-right font-mono text-brand text-base" data-testid="log-total">{total.toFixed(1)} h</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function CertificateModal({ student, onClose }) {
  const [cert, setCert] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [printPart, setPrintPart] = useState('all'); // all | cert | log

  useEffect(() => {
    const reset = () => setPrintPart('all');
    window.addEventListener('afterprint', reset);
    return () => window.removeEventListener('afterprint', reset);
  }, []);

  const printPartFn = (part) => {
    setPrintPart(part);
    setTimeout(() => window.print(), 60);
  };

  const refresh = () => api.get(`/students/${student.student_id}/certificate`).then(r => setCert(r.data));
  useEffect(() => { refresh(); }, [student.student_id]);

  const generateAi = async () => {
    setAiBusy(true);
    try {
      await api.post(`/students/${student.student_id}/ai-summary`);
      await refresh();
    } catch (e) {
      alert('AI summary failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  if (!cert) return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"><Loader2 className="w-8 h-8 animate-spin text-white"/></div>;

  const ai = cert.ai_summary || {};
  const kindLabel = cert.kind === 'volunteer' ? 'Volunteer Placement' : 'Practical Placement';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 print:bg-transparent print:p-0 overflow-y-auto" onClick={onClose} data-testid="cert-modal">
      <div className="bg-white shadow-xl w-full max-w-4xl p-10 border-8 border-double border-brand print:border-none print:shadow-none my-8" onClick={e=>e.stopPropagation()}>
        <style>{`@media print { .no-print { display:none !important; } body { background: white; } .print-hide { display:none !important; } .print-break-before { break-before: page; } }`}</style>
        <div className="no-print flex justify-end mb-4 gap-2 flex-wrap">
          <button onClick={generateAi} disabled={aiBusy} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="ai-summary-btn">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            {aiBusy ? 'Generating…' : (ai.paragraph ? 'Regenerate AI summary' : 'Generate AI summary')}
          </button>
          <button onClick={()=>printPartFn('cert')} className="btn-primary text-sm" data-testid="print-cert-part1">Print · Part 1 (certificate)</button>
          <button onClick={()=>printPartFn('log')} className="btn-primary text-sm" data-testid="print-cert-part2">Print · Part 2 (hours log)</button>
          <button onClick={()=>printPartFn('all')} className="btn-secondary text-sm" data-testid="print-cert-both">Print both</button>
          <button onClick={onClose} className="btn-secondary text-sm">Close</button>
        </div>

        {/* Letterhead */}
        <div className="flex items-start justify-between border-b border-fg/20 pb-4 mb-6">
          <div className="flex items-center gap-3">
            {cert.clinic?.logo_url && <img src={cert.clinic.logo_url} alt="" className="w-16 h-16 rounded"/>}
            <div>
              <div className="font-heading font-bold text-xl text-brand">{cert.clinic?.name}</div>
              {cert.clinic?.address && <div className="text-xs text-fg-muted">{cert.clinic.address}</div>}
              <div className="text-xs text-fg-muted">
                {cert.clinic?.phone || ''}{cert.clinic?.phone && cert.clinic?.email ? ' · ' : ''}{cert.clinic?.email || ''}{cert.clinic?.website ? ' · ' + cert.clinic.website : ''}
              </div>
            </div>
          </div>
          <div className="text-right text-[11px] text-fg-muted">
            <div>Issued · {cert.issued_on}</div>
            <div>Ref · {cert.student_id}</div>
          </div>
        </div>

        {/* ---- PART 1 — the certificate proper (award, AI summary, signatures) ---- */}
        <div className={printPart === 'log' ? 'print-hide' : ''} data-testid="cert-part1">
        <div className="text-center">
          <Award className="w-14 h-14 mx-auto text-brand"/>
          <h1 className="text-3xl md:text-4xl font-heading font-bold mt-4 text-fg">Certificate of {kindLabel}</h1>
          <p className="text-sm text-fg-muted mt-4">This certifies that</p>
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-fg mt-2">{cert.student_name}</h2>
          {cert.school && <p className="text-sm text-fg-muted mt-1">Student ID · {cert.student_id} · {cert.school} · {cert.academic_year}</p>}
          <p className="text-base text-fg mt-6 leading-relaxed max-w-2xl mx-auto">
            has completed <strong className="text-brand text-2xl">{cert.grand_total_hours}</strong> hours of
            supervised {cert.kind === 'volunteer' ? 'volunteer service' : 'practical training'} in <strong>{cert.clinical_area}</strong>
            {' '}from <strong>{cert.start_date}</strong> to <strong>{cert.end_date}</strong>.
          </p>
        </div>

        {/* AI Summary */}
        {ai.paragraph && (
          <div className="mt-8 max-w-3xl mx-auto">
            <h3 className="text-xs uppercase tracking-wider text-brand mb-2">Clinical exposure summary</h3>
            <p className="text-sm text-fg leading-relaxed">{ai.paragraph}</p>
          </div>
        )}
        {ai.evaluation && (ai.evaluation.strengths || ai.evaluation.growth_areas) && (
          <div className="mt-6 max-w-3xl mx-auto grid grid-cols-2 gap-6">
            {ai.evaluation.strengths?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wider text-brand mb-2">Strengths</h3>
                <ul className="list-disc pl-5 text-sm text-fg space-y-1">
                  {ai.evaluation.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {ai.evaluation.growth_areas?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wider text-brand mb-2">Areas for growth</h3>
                <ul className="list-disc pl-5 text-sm text-fg space-y-1">
                  {ai.evaluation.growth_areas.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Signature block — belongs to part 1 */}
        <div className="mt-10 grid grid-cols-2 gap-8 max-w-3xl mx-auto">
          <div>
            <div className="border-b-2 border-fg pb-1 h-12 font-heading italic text-brand">
              {cert.supervisor_name || ''}
            </div>
            <div className="text-xs text-fg-muted mt-1">Supervisor signature</div>
            <div className="text-xs text-fg font-semibold mt-1">{cert.supervisor_name || '—'}</div>
            <div className="text-xs text-fg-muted">{cert.supervisor_title || ''}</div>
          </div>
          <div>
            <div className="border-b-2 border-fg pb-1 h-12 font-heading text-fg-muted text-sm">
              {cert.clinic?.name}
            </div>
            <div className="text-xs text-fg-muted mt-1">Issued by</div>
            <div className="text-xs text-fg font-semibold mt-1">{cert.issued_on}</div>
          </div>
        </div>
        </div>{/* end part 1 */}

        {/* ---- PART 2 — hours appendix (session log) ---- */}
        {cert.session_log?.length > 0 && (
          <div className={printPart === 'cert' ? 'print-hide mt-8 max-w-3xl mx-auto' : `mt-8 max-w-3xl mx-auto ${printPart === 'all' ? 'print-break-before' : ''}`} data-testid="cert-part2">
            <h3 className="text-xs uppercase tracking-wider text-brand mb-2">Part 2 — Hours appendix · {cert.student_name} · Ref {cert.student_id}</h3>
            <table className="w-full text-xs border border-fg/10">
              <thead className="bg-soft text-fg-muted uppercase">
                <tr><th className="p-2 text-left">Date</th><th className="text-left">Clinical area</th><th className="text-right">Hours</th><th className="text-left">Supervisor</th></tr>
              </thead>
              <tbody>
                {cert.session_log.map((l, i) => (
                  <tr key={l.log_id || i} className="border-t border-fg/10">
                    <td className="p-2 font-mono">{l.date}</td>
                    <td>{l.clinical_area}</td>
                    <td className="text-right font-mono">{l.hours}</td>
                    <td>{l.supervisor_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-fg/40 font-semibold bg-soft/60">
                  <td colSpan={2} className="p-2 text-right uppercase text-[10px] tracking-wider">Logged hours</td>
                  <td className="text-right font-mono">{cert.log_hours}</td>
                  <td></td>
                </tr>
                {cert.timer_hours > 0 && (
                  <tr className="font-semibold bg-soft/40">
                    <td colSpan={2} className="p-2 text-right uppercase text-[10px] tracking-wider">Live-timer hours</td>
                    <td className="text-right font-mono">{cert.timer_hours}</td>
                    <td></td>
                  </tr>
                )}
                <tr className="border-t-2 border-fg font-bold text-brand bg-brand/5">
                  <td colSpan={2} className="p-2 text-right uppercase text-xs tracking-wider">Grand total</td>
                  <td className="text-right font-mono text-base">{cert.grand_total_hours} h</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-2 border-b border-soft pb-1">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children, full }) {
  return <label className={full ? 'md:col-span-2 block' : 'block'}>
    <div className="text-xs text-fg-muted mb-1">{label}</div>{children}
  </label>;
}
